// Moving the desktop app's window from any control in the top bar (the shell's own frame). The bar's empty space drags
// through the system (ui.css .gc-drag); its buttons and links take presses as controls. A press on one of them that
// moves past a few pixels while held becomes a window drag: the bar captures the pointer, the shell moves the window
// with the pointer's screen coordinates (the preload's windowDrag), and the click the release would make is not
// delivered. A press released without moving stays a click. A shell without windowDrag, and the browser, are left as
// they are.

/** how far a press moves (CSS px) before it is a window drag */
export const DRAG_THRESHOLD = 5;

/** The shell's call (gcDesktop.windowDrag). */
export type WindowDragCall = (phase: 'start' | 'move' | 'end', screenX: number, screenY: number) => void;

/** What the tracker reads of a pointer event. */
export interface BarPointer {
  pointerId: number;
  button: number;
  clientX: number;
  clientY: number;
  screenX: number;
  screenY: number;
}

/**
 * One press at a time on the bar: `down` arms it, `move` reports whether the press has just become a drag (the caller
 * captures the pointer then), `up` whether the click that follows must be swallowed.
 */
export class BarDragTracker {
  private press: { id: number; x: number; y: number } | null = null;
  private dragging = false;

  constructor(private readonly send: WindowDragCall) {}

  /** A press (the main button) on a control of the bar. */
  down(e: BarPointer): void {
    if (e.button !== 0) return;
    this.press = { id: e.pointerId, x: e.clientX, y: e.clientY };
    this.dragging = false;
  }

  /** The pointer moved: true when this move turns the press into a window drag. */
  move(e: BarPointer): boolean {
    const p = this.press;
    if (!p || e.pointerId !== p.id) return false;
    if (this.dragging) {
      this.send('move', e.screenX, e.screenY);
      return false;
    }
    if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < DRAG_THRESHOLD) return false;
    this.dragging = true;
    this.send('start', e.screenX, e.screenY);
    return true;
  }

  /** The press ended (released or cancelled): true when it was a drag, so its click is not delivered. */
  up(e: Pick<BarPointer, 'pointerId' | 'screenX' | 'screenY'>): boolean {
    const p = this.press;
    if (!p || e.pointerId !== p.id) return false;
    this.press = null;
    if (!this.dragging) return false;
    this.dragging = false;
    this.send('end', e.screenX, e.screenY);
    return true;
  }

  get active(): boolean {
    return this.dragging;
  }
}

/** The bars that are the window's title bar: the console's top bar, the administration console's, the boot strip. */
const BAR = '[data-bar="top"], [data-bar="admin"], .gc-titlestrip';
/** controls that need a press and a drag of their own (selecting text) */
const OWN_DRAG = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/** Swallows the next click (in the capture phase, before React's handlers), or none if none comes this task. */
function swallowNextClick(): void {
  const w = window;
  const opt = { capture: true };
  const stop = (e: Event) => {
    e.stopImmediatePropagation();
    e.preventDefault();
    w.removeEventListener('click', stop, opt);
  };
  w.addEventListener('click', stop, opt);
  w.setTimeout(() => w.removeEventListener('click', stop, opt), 0);
}

let started = false;

/** Starts the bar's drags in the shell's own frame (`windowDrag` from the shell's bridge, else nothing happens). */
export function startBarDrag(windowDrag: WindowDragCall | undefined): void {
  if (started || typeof windowDrag !== 'function') return;
  started = true;
  const root = document.documentElement;
  const t = new BarDragTracker(windowDrag);
  let bar: Element | null = null;
  window.addEventListener(
    'pointerdown',
    (e) => {
      if (!root.classList.contains('gc-frame') || e.pointerType === 'touch') return;
      const el = e.target instanceof Element ? e.target : null;
      const b = el?.closest(BAR) ?? null;
      if (!b || el?.closest(OWN_DRAG)) return;
      bar = b;
      t.down(e);
    },
    true,
  );
  window.addEventListener(
    'pointermove',
    (e) => {
      if (!t.move(e)) return;
      // the drag starts: the bar keeps the pointer's events while the press is held, nothing is selected or hovered
      try {
        bar?.setPointerCapture(e.pointerId);
      } catch {
        /* the pointer is gone already: its release ends the drag */
      }
      window.getSelection()?.removeAllRanges();
      root.style.userSelect = 'none';
    },
    true,
  );
  const end = (e: PointerEvent) => {
    if (!t.up(e)) return;
    root.style.userSelect = '';
    try {
      if (bar?.hasPointerCapture(e.pointerId)) bar.releasePointerCapture(e.pointerId);
    } catch {
      /* released with the pointer */
    }
    bar = null;
    if (e.type === 'pointerup') swallowNextClick();
  };
  window.addEventListener('pointerup', end, true);
  window.addEventListener('pointercancel', end, true);
}
